<script setup>
import { computed, ref, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['sendMessage', 'back']);

const { t } = useI18n();

const processedParams = ref({});
const mediaId = computed(() => JSON.parse(props.template.meta).mediaId);
const mediaUrl = computed(() => JSON.parse(props.template.meta).mediaUrl);
const allowedFormats = computed(() => [
  'link_video',
  'link_image',
  'link_document',
  'link_gif'
]);

const hasImage = computed(() => props.template.templateType === 'IMAGE');
const hasVideo = computed(() => ['VIDEO', 'GIF'].includes(props.template.templateType));
const hasDocument = computed(() => props.template.templateType === 'DOCUMENT');

const mediaSource = computed(() => {
  if (mediaUrl.value === undefined || mediaUrl.value === null) {
    return `https://api.gupshup.io/wa/${props.template.app_id}/wa/media/${mediaId.value}?download=false`;
  }
  return mediaUrl.value;
});

const hasMediaHeader = computed(() => {
  return props.template?.templateType !== 'TEXT';
});

const preventBraces = (event) => {
  if (event.key === '{' || event.key === '}') {
    event.preventDefault();
  }
};

const handlePaste = (event) => {
  event.preventDefault();

  const pastedText = (event.clipboardData || window.clipboardData).getData('text');

  const sanitizedText = pastedText.replace(/[{}]/g, '');

  const inputElement = event.target;
  const variableItem = inputElement.closest('.flex.items-center.w-full.gap-2');
  const variableLabel = variableItem.querySelector('span');
  const key = variableLabel.textContent.trim();

  const currentValue = processedParams.value[key] || '';
  const newValue = currentValue + sanitizedText;

  processedParams.value[key] = newValue;
};

const templateName = computed(() => {
  return props.template?.name || '';
});

const originalTemplateString = computed(() => {
  return props.template?.components?.find(
    component => component.type === 'BODY'
  ).text;
});

const templateMediaVariable = computed(() => {
  if (hasMediaHeader.value) {
    return '{{link_' + props.template.templateType.toLowerCase() + '}}';
  }
  return '';
});

const templateString = computed(() => {
  return templateMediaVariable.value + originalTemplateString.value;
});

const processVariable = str => {
  return str.replace(/{{|}}/g, '');
};

const processedString = computed(() => {
  return originalTemplateString.value.replace(/{{([^}]+)}}/g, (match, variable) => {
      const variableKey = processVariable(variable);
      return processedParams.value[variableKey] || `{{${variable}}}`;
  });
});

const processedStringWithVariableHighlight = computed(() => {
  const variables = originalTemplateString.value.match(/{{([^}]+)}}/g) || [];

  return variables.reduce((result, variable) => {
    const variableKey = processVariable(variable);
    const value = processedParams.value[variableKey] || variable;
    return result.replace(
      variable,
      `<span class="break-all text-n-slate-12">${value}</span>`
    );
  }, originalTemplateString.value);
});

const rules = computed(() => {
  const paramRules = {};
  Object.keys(processedParams.value).forEach(key => {
    paramRules[key] = { required: requiredIf(true) };
  });
  return {
    processedParams: paramRules,
  };
});

const v$ = useVuelidate(rules, { processedParams });

const getFieldErrorType = key => {
  if (!v$.value.processedParams[key]?.$error) return 'info';
  return 'error';
};

const generateVariables = () => {
  const matchedVariables = templateString.value.match(/{{([^}]+)}}/g);
  if (!matchedVariables) return;

  const finalVars = matchedVariables.map(i => processVariable(i));
  processedParams.value = finalVars.reduce((acc, variable) => {
    acc[variable] = allowedFormats.value.includes(variable)
      ? mediaSource.value
      : '';
    return acc;
  }, {});
};

const sendMessage = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  const payload = {
    message: processedString.value,
    templateParams: {
      name: props.template.name,
      category: props.template.category,
      language: props.template.language,
      namespace: props.template.namespace,
      processed_params: processedParams.value,
      template_id: props.template.id,
    },
  };
  emit('sendMessage', payload);
};

onMounted(() => {
  generateVariables();
});
</script>

<template>
  <div
    class="absolute top-full mt-1.5 max-h-[50vh] overflow-y-auto left-0 flex flex-col gap-4 px-4 pt-6 pb-5 items-start w-[460px] h-auto bg-n-solid-2 border border-n-strong shadow-sm rounded-lg"
  >
    <span class="text-sm text-n-slate-12">
      {{
        t(
          'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.TEMPLATE_NAME',
          { templateName: templateName }
        )
      }}
    </span>
    <div class="w-full">
      <div class="variable-image-container">
        <img
          v-if="hasImage"
          :src="mediaSource"
          alt="Image Preview"
          class="image-preview"
        />
        <video
          v-if="hasVideo"
          controls
          autoplay
          name="media"
          class="image-preview"
        >
          <source :src="mediaSource" type="video/mp4" />
        </video>
        <div v-if="hasDocument" class="variable-document-container">
          <div class="document-placeholder">
            <span class="document-icon">📄</span>
            <a
              :href="mediaSource"
              target="_blank"
              rel="noopener noreferrer"
              class="document-link"
            >
              {{
                t(
                  'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.DOCUMENT'
                )
              }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <p
      class="mb-0 text-sm text-n-slate-11"
      v-html="processedStringWithVariableHighlight"
    />

    <span
      v-if="Object.keys(processedParams).length"
      class="text-sm font-medium text-n-slate-12"
    >
      {{
        t(
          'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.VARIABLES'
        )
      }}
    </span>

    <div
      v-for="(variable, key) in processedParams"
      :key="key"
      class="flex items-center w-full gap-2"
    >
      <span
        class="flex items-center h-8 text-sm min-w-6 ltr:text-left rtl:text-right text-n-slate-10"
      >
        {{ key }}
      </span>
      <Input
        v-model="processedParams[key]"
        @keydown="preventBraces"
        @paste="handlePaste"
        custom-input-class="!h-8 w-full !bg-transparent"
        class="w-full"
        :disabled="allowedFormats.includes(key)"
        :message-type="getFieldErrorType(key)"
      />
    </div>

    <div class="flex items-end justify-between w-full gap-3 h-14">
      <Button
        :label="
          t(
            'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.BACK'
          )
        "
        color="slate"
        variant="faded"
        class="w-full font-medium"
        @click="emit('back')"
      />
      <Button
        :label="
          t(
            'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.TEMPLATE_PARSER.SEND_MESSAGE'
          )
        "
        class="w-full font-medium"
        @click="sendMessage"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.variable-image-container {
  @apply flex flex-col;

  .image-preview {
    @apply mb-2 w-full h-auto rounded-md border border-slate-300 dark:border-slate-600;
    max-height: 200px;
    object-fit: cover;
  }
}

.variable-document-container {
  @apply mb-2;
  .document-placeholder {
    @apply mt-2 flex items-center justify-center gap-1 p-4 border border-slate-300 dark:border-slate-600 rounded-md;

    .document-icon {
      @apply text-2xl text-slate-700 dark:text-slate-100;
    }

    .document-link {
      @apply text-sm text-slate-500 underline dark:text-slate-400 cursor-pointer;
    }
  }
}
</style>
